/* KitzeyBee accessibility helpers.
   1. Adds a pause/play button to autoplaying background videos (WCAG 2.2.2 Pause, Stop, Hide).
   2. Adds a pause/play button to the rotating announcement bar, and stops it announcing itself to screen readers while it rotates.
   3. Respects the visitor's "reduce motion" setting: videos and rotation start paused.
   4. Fixes a few spots where the theme leaves elements unnamed, hidden from screen readers, or mislabeled. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var ICON_PAUSE =
    '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path fill="currentColor" d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z"/></svg>';
  var ICON_PLAY =
    '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path fill="currentColor" d="M8 5.5v13l11-6.5z"/></svg>';

  function makeButton(kind) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'kb-motion-toggle kb-motion-toggle--' + kind;
    return button;
  }

  function setButton(button, paused, label) {
    button.innerHTML = paused ? ICON_PLAY : ICON_PAUSE;
    button.setAttribute('aria-label', (paused ? 'Play ' : 'Pause ') + label);
  }

  function setupVideo(video) {
    if (video.dataset.kbMotion) return;
    var host = video.parentElement;
    if (!host) return;
    video.dataset.kbMotion = 'true';

    var button = makeButton('video');

    function sync() {
      setButton(button, video.paused, 'background video');
    }

    button.addEventListener('click', function () {
      if (video.paused) {
        var attempt = video.play();
        if (attempt && attempt.catch) attempt.catch(function () {});
      } else {
        video.pause();
      }
    });

    video.addEventListener('play', sync);
    video.addEventListener('pause', sync);
    host.appendChild(button);

    /* If the video is paused (reduce motion, low power mode, or autoplay blocked), show its still frame
       instead of an empty box. */
    var fallback = video.querySelector('img');
    if (fallback && fallback.getAttribute('src') && !video.getAttribute('poster')) {
      video.setAttribute('poster', fallback.getAttribute('src'));
    }

    if (reduceMotion) {
      video.removeAttribute('autoplay');
      video.pause();
    }
    sync();
  }

  function setupAnnouncementBar(bar) {
    if (bar.dataset.kbMotion) return;
    bar.dataset.kbMotion = 'true';

    var button = makeButton('announcement');
    var paused = false;

    function apply(nextPaused) {
      paused = nextPaused;
      if (paused) {
        if (typeof bar.pause === 'function') bar.pause();
        bar.setAttribute('aria-live', 'polite');
      } else {
        if (typeof bar.play === 'function') bar.play();
        bar.setAttribute('aria-live', 'off');
      }
      setButton(button, paused, 'announcements');
    }

    button.addEventListener('click', function () {
      apply(!paused);
    });

    /* Keyboard users: hold the rotation while focus is inside the bar. */
    bar.addEventListener('focusin', function () {
      if (!paused && typeof bar.suspend === 'function') bar.suspend();
    });
    bar.addEventListener('focusout', function () {
      if (!paused && typeof bar.resume === 'function') bar.resume();
    });

    bar.appendChild(button);
    apply(reduceMotion);
  }

  function fixMarkup() {
    /* Images inside a video element are only a fallback and should not be read out. */
    document.querySelectorAll('video img:not([alt])').forEach(function (img) {
      img.setAttribute('alt', '');
    });

    /* A group block with a link renders an empty overlay link. Name it from the group's heading. */
    document.querySelectorAll('a.group-block__link:not([aria-label]):not([aria-labelledby])').forEach(function (link) {
      if (link.textContent.trim()) return;
      var scope = link.closest('.group-block') || link.parentElement;
      var heading = scope && scope.querySelector('h1, h2, h3, h4, h5, h6');
      var name = heading ? heading.textContent.replace(/\s+/g, ' ').trim() : '';
      if (name) link.setAttribute('aria-label', name);
    });

    /* The product gallery thumbnails use aria-selected on plain buttons. Give them the tab roles that make that valid. */
    document.querySelectorAll('button[ref="thumbnails[]"][aria-selected]:not([role])').forEach(function (button) {
      button.setAttribute('role', 'tab');
      var list = button.parentElement;
      if (list && !list.getAttribute('role')) list.setAttribute('role', 'tablist');
    });

    /* Labels need a role to be valid on a plain div (loading placeholders, the collection filter panel). */
    document
      .querySelectorAll('div[aria-label="Loading product recommendations"]:not([role]), div[aria-label="Filters"]:not([role])')
      .forEach(function (el) {
        el.setAttribute('role', 'group');
      });

    /* A carousel with room for every card still marks the extra cards as hidden from screen readers.
       If a card is fully in view, it should be readable. */
    document.querySelectorAll('.resource-list__slide[aria-hidden="true"]').forEach(function (slide) {
      var holder = slide.parentElement;
      if (!holder) return;
      var box = holder.getBoundingClientRect();
      var rect = slide.getBoundingClientRect();
      var inView =
        rect.width > 0 && rect.left >= box.left - 1 && rect.right <= box.right + 1 && rect.right <= window.innerWidth + 1;
      if (inView) slide.setAttribute('aria-hidden', 'false');
    });

    /* Keep the visual size of these headings but give them a level that follows the page's h1. */
    document.querySelectorAll('h3, h4').forEach(function (heading) {
      if (heading.hasAttribute('aria-level')) return;
      var text = heading.textContent.trim();
      if (/^you might also like/i.test(text) || heading.classList.contains('facets--filters-title')) {
        heading.setAttribute('aria-level', '2');
      }
    });
  }

  function init() {
    document.querySelectorAll('video[autoplay]').forEach(setupVideo);

    document.querySelectorAll('announcement-bar-component[autoplay]').forEach(function (bar) {
      customElements.whenDefined('announcement-bar-component').then(function () {
        setupAnnouncementBar(bar);
      });
    });

    fixMarkup();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  /* Content that loads later (recommendations, filters, carousels) gets the same fixes. */
  var pending = null;
  new MutationObserver(function () {
    if (pending) return;
    pending = setTimeout(function () {
      pending = null;
      fixMarkup();
    }, 200);
  }).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-hidden']
  });
})();
